import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { trackLeadConversion } from "@/lib/analytics";

const STORAGE_KEY = "datorra_scroll_popup_shown_v1";
const SCROLL_THRESHOLD = 0.05; // 5%

export default function ScrollPopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const progress = window.scrollY / total;
      if (progress >= SCROLL_THRESHOLD) {
        setOpen(true);
        sessionStorage.setItem(STORAGE_KEY, "1");
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({
        name: form.name,
        email: form.email,
        phone: form.phone,
        requirement: "Scroll Popup Lead",
        message: "Captured via home-page scroll popup.",
        source: "home-scroll-popup",
      });
      trackLeadConversion();
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
      setTimeout(close, 2000);
    } catch (err) {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="scroll-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] flex items-center justify-center px-4"
        >
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={close}
            data-testid="scroll-popup-backdrop"
          />
          <motion.div
            initial={{ y: 32, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.2, 0.7, 0.15, 1] }}
            className="relative w-full max-w-md bg-white rounded-3xl border border-slate-100 shadow-2xl p-8 md:p-10"
          >
            <button
              data-testid="scroll-popup-close"
              onClick={close}
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:border-slate-900 transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>

            {done ? (
              <div className="flex flex-col items-center text-center py-6">
                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                  <Check className="w-6 h-6 text-blue-600" strokeWidth={1.75} />
                </div>
                <div className="mt-5 font-display text-2xl text-slate-900">We've got it.</div>
                <div className="mt-2 text-sm text-slate-500">Our team will reach out shortly.</div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Get a Quote
                </div>
                <h3 className="mt-3 font-display text-3xl md:text-4xl font-light tracking-tight text-slate-900 leading-[1.05]">
                  Scope your <span className="font-serif-editorial italic">database</span> in 24 hours.
                </h3>
                <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                  Share your details — we'll reply with a transparent quote for your exact ICP, geography and filters.
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="scroll-popup-form">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">Name*</label>
                    <input
                      data-testid="scroll-popup-name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2.5 text-slate-900 text-base outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">Number*</label>
                    <input
                      data-testid="scroll-popup-phone"
                      required
                      inputMode="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2.5 text-slate-900 text-base outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">Email*</label>
                    <input
                      data-testid="scroll-popup-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2.5 text-slate-900 text-base outline-none transition-colors"
                    />
                  </div>

                  <button
                    data-testid="scroll-popup-submit"
                    disabled={loading}
                    className="mt-4 group w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 transition-all disabled:opacity-60"
                  >
                    {loading ? "Sending…" : "Get My Quote"}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </button>
                  <div className="text-[11px] text-slate-400 text-center mt-2">
                    We respect your data. No spam — ever.
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
